import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl, streamUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, Trophy, Play, PlayCircle, Loader2 } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
};

export default function MyBoosts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api
      .get("/me/boosts-given")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user]);

  const replay = (t) => {
    window.dispatchEvent(
      new CustomEvent("neosync:gift-preview", {
        detail: {
          mode: "sent",
          amount_cents: t.amount_cents,
          tipper_name: t.creator?.name || `@${t.creator?.username}` || "Creator",
          tipper_picture: t.creator?.picture,
          message: t.message,
        },
      })
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-mono text-xs uppercase tracking-widest">
        sign in to see your boosts
      </div>
    );
  }
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
          // your.boosts
        </p>
        <h1
          className="font-display font-black text-4xl md:text-5xl mb-3 flex items-center gap-3 glitch"
          data-text="TROPHY SHELF"
        >
          <Trophy className="h-7 w-7 text-[#FFB800]" strokeWidth={2} fill="#FFB800" />
          TROPHY SHELF
        </h1>
        <p className="text-white/40 text-sm font-mono">
          every creator you've backed · tap a boost to replay the animation
        </p>

        {/* Totals hero */}
        {data && data.total_count > 0 && (
          <section
            data-testid="my-boosts-summary"
            className="mt-6 grid grid-cols-2 gap-3"
          >
            <div className="p-4 border border-[#FF007F]/40 bg-[#FF007F]/5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF007F]">
                total.sent
              </p>
              <p className="font-display font-black text-3xl text-white mt-1">
                ${data.total_usd.toFixed(2)}
              </p>
            </div>
            <div className="p-4 border border-white/10 bg-[#0A0A0C]">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                boosts.given
              </p>
              <p className="font-display font-black text-3xl text-white mt-1">
                {data.total_count}
              </p>
            </div>
          </section>
        )}

        {/* List */}
        {!data || data.total_count === 0 ? (
          <div
            data-testid="my-boosts-empty"
            className="mt-14 py-16 text-center border border-white/10 bg-[#0A0A0C]"
          >
            <Zap className="h-10 w-10 mx-auto text-[#FF007F]/50 mb-3" strokeWidth={1.2} />
            <p className="font-display font-bold text-white/80 text-lg">
              you haven't boosted anyone yet
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-2">
              // find a creator you love and hit the BOOST button
            </p>
          </div>
        ) : (
          <ul className="mt-8 space-y-3" data-testid="my-boosts-list">
            {data.boosts.map((b) => (
              <li
                key={b.id}
                data-testid={`my-boost-${b.id}`}
                className="flex gap-3 p-3 border border-white/10 bg-[#0A0A0C] hover:border-[#FF007F]/40 transition-colors"
              >
                {b.video?.deleted ? (
                  <div className="w-20 h-28 bg-black/50 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-mono text-[9px] uppercase text-white/30 text-center px-2">
                      video<br />deleted
                    </span>
                  </div>
                ) : (
                  <Link
                    to={`/v/${b.video?.id}`}
                    className="w-20 h-28 bg-black overflow-hidden border border-white/10 relative group flex-shrink-0"
                  >
                    <video
                      src={streamUrl(b.video?.id)}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                      <Play className="h-6 w-6 text-white" fill="white" />
                    </div>
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {b.creator?.picture ? (
                      <img
                        src={picUrl(b.creator.picture)}
                        alt=""
                        className="h-6 w-6 object-cover border border-white/10 flex-shrink-0"
                      />
                    ) : (
                      <div className="h-6 w-6 bg-[#00FFFF]/20 flex items-center justify-center text-[#00FFFF] font-bold text-[10px] flex-shrink-0">
                        {(b.creator?.name || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <Link
                      to={`/u/${b.creator?.username || ""}`}
                      className="text-white text-sm font-semibold truncate hover:text-[#00FFFF]"
                    >
                      {b.creator?.name || "Creator"}
                    </Link>
                  </div>
                  <p className="text-xs text-white/50 truncate">
                    {b.video?.caption || "—"}
                  </p>
                  {b.message && (
                    <p className="text-xs text-white/40 italic truncate mt-1">
                      “{b.message}”
                    </p>
                  )}
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-1.5">
                    {timeAgo(b.created_at)} ago
                  </p>
                </div>
                <div className="flex flex-col items-end justify-between flex-shrink-0 gap-2">
                  <p className="font-display font-black text-lg text-[#FF007F] flex items-center gap-1">
                    <Zap className="h-3.5 w-3.5" fill="#FF007F" strokeWidth={0} />
                    ${(b.amount_cents / 100).toFixed(2)}
                  </p>
                  {b.amount_cents >= 500 && (
                    <button
                      data-testid={`replay-${b.id}`}
                      onClick={() => replay(b)}
                      className="flex items-center gap-1 text-[10px] uppercase font-mono tracking-widest px-2.5 py-1 border border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10 hover:shadow-[0_0_12px_rgba(255,0,127,0.3)] transition-all"
                    >
                      <PlayCircle className="h-3 w-3" />
                      replay
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
